import type { ReactNode } from 'react';
import type { AiMessage } from '@apis/aiAgent';
import { AiAnswerLinks } from '@components/ui/AiAnswerLinks/AiAnswerLinks';
import { AiAnswerReceipt } from '@components/ui/AiAnswerReceipt/AiAnswerReceipt';
import { checksOf, linksOf } from '@utils/aiChat';
import styles from './styles.module.css';

interface IAiAnswerTrailProps {
  /** The conversation's site — the links open on it. */
  siteId: string;
  /** A COMPLETE answer: the caller holds both back while it is still arriving. */
  message: Pick<AiMessage, 'checks' | 'links' | 'at'>;
  /** The site's zone, for the receipt's time. */
  tz?: string;
  /** What the surface lets the reader do with the answer — the page's thumbs — on the receipt's line. */
  actions?: ReactNode;
}

/**
 * What trails a complete answer on both chat surfaces: where it offers to take
 * the reader, and under that one line about the answer — the receipt, what it
 * checked, with the surface's actions beside it.
 *
 * The links come first because they are part of the answer — the record it was
 * about, one tap away. The receipt and the thumbs are ABOUT the answer, so they
 * share one muted line rather than each taking a row of its own; the next
 * questions are not here at all but above the composer (`AiFollowUps`).
 * In that order, in one place, so the page and the dashboard card cannot drift
 * into two different footers for the same answer.
 */
export function AiAnswerTrail({ siteId, message, tz, actions }: IAiAnswerTrailProps) {
  const checks = checksOf(message);
  return (
    <>
      <AiAnswerLinks siteId={siteId} links={linksOf(message)} />
      {/* Not even an empty line when there is nothing to put on it: inside the
          surface's column an empty element would still take a gap. */}
      {(checks.length > 0 || actions !== undefined) && (
        <div className={styles.meta}>
          <AiAnswerReceipt checks={checks} at={message.at} tz={tz} />
          {actions}
        </div>
      )}
    </>
  );
}
